<template>
  <!-- Maintenance Mode -->
  <div v-if="isOnMaintenance && !maintenanceBypassed" class="maintenance-container">
    <div class="maintenance-card">
      <!-- Icon -->
      <div class="maintenance-icon-wrapper">
        <svg xmlns="http://www.w3.org/2000/svg" class="maintenance-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </div>

      <!-- Title -->
      <h2 class="maintenance-title">Уважаемые абитуриенты и их родители!</h2>

      <!-- Text -->
      <p class="maintenance-text">
        На данный момент мы выполняем обновление калькуляторов в рамках подготовки к новым правилам приёма 2026!
      </p>
      <p class="maintenance-text">
        Совсем скоро вы сможете вновь использовать виджеты, возвращайтесь чуть позже!
      </p>
    </div>
  </div>

  <!-- Main content -->
  <template v-else>
    <AchievementsViewerWidget 
        v-if="currentView === 'achievements'"
        :domain="domain"
        :storageUri="storageUri"
        :isApp="isApp"
        @height-update="handleHeightUpdate"
      />
      <ProgramsByExamsView
        v-else-if="currentView === 'programsByExams'"
        :domain="domain"
        :storageUri="storageUri"
        :isApp="isApp"
        style="width: 100vw;"
      />
      <DemoView
        v-else-if="currentView === 'demo'"
        :domain="domain"
        :storageUri="storageUri"
        :isApp="isApp"
      />
  </template>
  <main>
   

    <!-- Widget Selector Modal -->
    <ion-modal 
      :is-open="showWidgetSelector" 
      @didDismiss="closeWidgetSelector"
      :breakpoints="[0, 1]"
      :initial-breakpoint="1"
    >
      <ion-header>
        <ion-toolbar>
          <ion-title>Выберите виджет</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="closeWidgetSelector">
              <ion-icon :icon="closeIcon"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
        <div class="p-4 space-y-4">
          <div 
            v-for="widget in availableWidgets" 
            :key="widget.id"
            @click="selectWidget(widget.id)"
            class="w-full p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 text-left border border-gray-200 cursor-pointer"
            :class="{ 'border-blue-500 bg-blue-50': currentView === widget.id }"
          >
            <div class="flex items-center gap-3">
              <div class="p-2 bg-blue-100 rounded-lg">
                <component :is="widget.icon" class="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 class="font-medium text-gray-900">{{ widget.title }}</h3>
                <p class="text-sm text-gray-600">{{ widget.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </ion-header>

      <ion-content>
        
      </ion-content>
    </ion-modal>
  </main>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import AchievementsViewerWidget from './AchievementsViewerWidget.vue';
import ProgramsByExamsView from './ProgramsByExamsView.vue';
import DemoView from './DemoView.vue';
import { IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonContent } from '@ionic/vue';
import { close } from 'ionicons/icons';

// Define widget icons as components
const CheckCircleIcon = defineComponent({
  template: `
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  `
});

const DocumentIcon = defineComponent({
  template: `
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  `
});

const DemoIcon = defineComponent({
  template: `
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  `
});

export default defineComponent({
  name: 'MainView',
  
  components: {
    AchievementsViewerWidget,
    ProgramsByExamsView,
    DemoView,
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    IonContent,
    CheckCircleIcon,
    DocumentIcon,
    DemoIcon
  },

  props: {
    domain: {
      type: String,
      required: true
    },
    storageUri: {
      type: String,
      required: true,
      default: ""
    },
    isApp: {
      type: Boolean,
      default: false
    },
    requestedView: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      title: 'Калькулятор индивидуальных достижений',
      subtitle: 'Рассчитайте дополнительные баллы для поступления в НИУ ВШЭ',
      widgetHeight: 0,
      isLoading: true,
      currentView: 'achievements',
      showWidgetSelector: false,
      keyPressCount: 0,
      lastKeyPressTime: 0,
      keyPressTimeout: null as number | null,
      closeIcon: close,
      // Maintenance mode
      isOnMaintenance: false,
      maintenanceBypassed: false,
      maintenanceKeyPressCount: 0,
      maintenanceLastKeyPressTime: 0,
      maintenanceKeyPressTimeout: null as number | null,
      availableWidgets: [
        {
          id: 'achievements',
          title: 'Калькулятор достижений',
          description: 'Рассчитайте дополнительные баллы за индивидуальные достижения',
          icon: CheckCircleIcon
        },
        {
          id: 'programsByExams',
          title: 'Программы по экзаменам',
          description: 'Подберите программы по выбранным экзаменам',
          icon: DocumentIcon
        },
        {
          id: 'demo',
          title: 'Демо виджет',
          description: 'Демонстрационный виджет для тестирования',
          icon: DemoIcon
        }
      ]
    }
  },

  computed: {
    isMobile(): boolean {
      return window.innerWidth < 768
    }
  },

  watch: {
    widgetHeight(newHeight: number) {
      this.updateParentHeight(newHeight)
    },
    requestedView: {
      immediate: true,
      handler(newView: string) {
        if (newView) {
          this.currentView = newView
        }
        this.$nextTick(() => {
          if (window.parent && window.parent !== window) {
            window.parent.postMessage({ height: document.body.scrollHeight }, '*')
          }
        })
      }
    },
    currentView() {
      this.$nextTick(() => {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ height: document.body.scrollHeight }, '*')
        }
      })
    }
  },

  methods: {
    handleHeightUpdate(height: number) {
      this.widgetHeight = height
    },

    updateParentHeight(height: number) {
      if (window.parent) {
        window.parent.postMessage({ height }, '*')
      }
    },

    checkMobile() {
      this.isMobile = window.innerWidth < 768
    },

    handleKeyPress(event: KeyboardEvent) {
      const currentTime = Date.now()
      
      // Reset count if more than 1 second has passed since last press
      if (currentTime - this.lastKeyPressTime > 1000) {
        this.keyPressCount = 0
      }
      
      // Update last press time
      this.lastKeyPressTime = currentTime
      
      // Check if 'a' was pressed
      if (event.key.toLowerCase() === 'a') {
        this.keyPressCount++
        
        // Clear existing timeout
        if (this.keyPressTimeout) {
          window.clearTimeout(this.keyPressTimeout)
        }
        
        // Set new timeout to reset count
        this.keyPressTimeout = window.setTimeout(() => {
          this.keyPressCount = 0
        }, 1000)
        
        // Show selector if 4 presses detected
        if (this.keyPressCount === 4) {
          this.showWidgetSelector = true
          this.keyPressCount = 0
        }
      }

      // Check if 'i' was pressed for maintenance bypass
      if (event.key.toLowerCase() === 'i') {
        // Reset count if more than 1 second has passed
        if (currentTime - this.maintenanceLastKeyPressTime > 1000) {
          this.maintenanceKeyPressCount = 0
        }
        
        this.maintenanceLastKeyPressTime = currentTime
        this.maintenanceKeyPressCount++
        
        // Clear existing timeout
        if (this.maintenanceKeyPressTimeout) {
          window.clearTimeout(this.maintenanceKeyPressTimeout)
        }
        
        // Set new timeout to reset count
        this.maintenanceKeyPressTimeout = window.setTimeout(() => {
          this.maintenanceKeyPressCount = 0
        }, 1000)
        
        // Bypass maintenance if 5 presses detected
        if (this.maintenanceKeyPressCount === 5) {
          this.maintenanceBypassed = true
          this.maintenanceKeyPressCount = 0
          console.log('🔓 Maintenance bypassed for developer access')
        }
      }
    },

    closeWidgetSelector() {
      this.showWidgetSelector = false
    },

    selectWidget(widgetId: string) {
      this.currentView = widgetId
      this.closeWidgetSelector()
    }
  },

  mounted() {
    console.log('MainView mounted')
    this.isLoading = false
    
    // Add resize listener
    window.addEventListener('resize', this.checkMobile)
    
    // Add keyboard listener
    window.addEventListener('keydown', this.handleKeyPress)
    
    // Initial mobile check
    this.checkMobile()

    // Check URL query parameters for currentView
    const urlParams = new URLSearchParams(window.location.search);
    const viewFromQuery = urlParams.get('currentView');
    
    // Set currentView based on priority: prop > query > default
    if (this.requestedView) {
      this.currentView = this.requestedView;
    } else if (viewFromQuery && ['achievements', 'programsByExams', 'demo'].includes(viewFromQuery)) {
      this.currentView = viewFromQuery;
    }

    console.log('Current View:', this.currentView)
  },

  beforeUnmount() {
    // Clean up event listeners
    window.removeEventListener('resize', this.checkMobile)
    window.removeEventListener('keydown', this.handleKeyPress)
    
    // Clear any existing timeout
    if (this.keyPressTimeout) {
      window.clearTimeout(this.keyPressTimeout)
    }
    if (this.maintenanceKeyPressTimeout) {
      window.clearTimeout(this.maintenanceKeyPressTimeout)
    }
  }
});
</script>

<style scoped>
/* Add any main view specific styles here */
.container {
  max-width: 1280px;
  margin: 0 auto;
}

@media (max-width: 768px) {
  .container {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}

/* Modal styles */
ion-modal {
  --width: 100%;
  --height: 100%;
  --border-radius: 16px 16px 0 0;
}

@media (min-width: 768px) {
  ion-modal {
    --width: 500px;
    --height: auto;
    --border-radius: 16px;
  }
}

ion-modal::part(content) {
  --background: white;
}

ion-modal::part(header) {
  --background: white;
  border-bottom: 1px solid #e5e7eb;
}

/* Ensure content is visible */
.ion-padding {
  padding: 16px !important;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

/* Widget card styles */
.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  transform: translateY(-1px);
}

.cursor-pointer:active {
  transform: translateY(0);
}

/* Maintenance screen - Apple style */
.maintenance-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.maintenance-card {
  width: 100%;
  max-width: 800px;
  background: #ffffff;
  border-radius: 18px;
  padding: 48px 40px;
  text-align: center;
  box-shadow: 
    0 0 0 0.5px rgba(0, 0, 0, 0.05),
    0 1px 2px rgba(0, 0, 0, 0.04),
    0 4px 8px rgba(0, 0, 0, 0.04),
    0 12px 28px rgba(0, 0, 0, 0.06),
    0 24px 50px rgba(0, 0, 0, 0.08);
}

.maintenance-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: linear-gradient(180deg, #fafafa 0%, #f0f0f0 100%);
  border-radius: 14px;
  margin-bottom: 24px;
  box-shadow: 
    0 0 0 0.5px rgba(0, 0, 0, 0.04),
    0 2px 4px rgba(0, 0, 0, 0.04);
}

.maintenance-icon {
  width: 28px;
  height: 28px;
  color: #6e6e73;
}

.maintenance-title {
  font-size: 29px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 16px 0;
  line-height: 1.25;
  letter-spacing: -0.01em;
}

.maintenance-text {
  font-size: 24px;
  font-weight: 400;
  color: #6e6e73;
  margin: 0 0 8px 0;
  line-height: 1.47;
  letter-spacing: -0.01em;
}

.maintenance-text:last-child {
  margin-bottom: 0;
}

@media (max-width: 480px) {
  .maintenance-container {
    padding: 16px;
  }
  
  .maintenance-card {
    padding: 36px 28px;
    border-radius: 16px;
  }
  
  .maintenance-title {
    font-size: 19px;
  }
  
  .maintenance-text {
    font-size: 14px;
  }
}
</style> 